import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, UserRound, Info as InfoIcon, Loader2, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { errText } from "./kit";

// V3 building blocks (C101–C120): breadcrumb, field cards, comparison, check rows, context column and action pair.
export const PRUEFSTAND = "Entscheidung und erforderliche Nachweise vor einer Bestätigung prüfen.";
export const VALUE_TEXT = { ja: "Vorhanden", nein: "Nicht vorhanden", unbekannt: "Unbekannt" };

export function V3Page({ crumb, title, subtitle, aside, children, testId }) {
  const { user } = useAuth();
  return (
    <div data-testid={testId} className="animate-fade-in-up">
      <p className="hidden text-xs font-semibold text-zinc-500 lg:block" data-testid="v3-crumb">{crumb} / Plattformverwaltung</p>
      <p className="text-xs font-semibold text-zinc-500 lg:hidden">{user?.name} · {user?.role_label}</p>
      <h1 className="mt-3 break-words text-3xl font-bold leading-tight tracking-tight text-brand-purple hyphens-auto lg:text-[2.15rem]" lang="de">{title}</h1>
      {subtitle && <p className="mt-2 text-base text-zinc-600">{subtitle}</p>}
      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_18.5rem]">
        <div className="min-w-0 space-y-2.5">{children}</div>
        {aside && <aside className="hidden space-y-4 lg:block" aria-label="Kontext">{aside}</aside>}
      </div>
    </div>
  );
}

function AsideCard({ icon: Icon, title, text, tone, testId }) {
  const cls = { lavender: "border-[#E3DDF0] bg-[#EEEAF7]", amber: "border-[#F6E3B4] bg-[#FFF6DD]" }[tone] || "border-[#E6E1F0] bg-white";
  return (
    <div data-testid={testId} className={`rounded-2xl border p-4 ${cls}`}>
      <p className={`flex items-center gap-2 font-bold ${tone === "amber" ? "text-[#8A5A00]" : "text-brand-purple"}`}>
        <Icon className={`h-5 w-5 ${tone === "amber" ? "" : "text-brand-magenta"}`} aria-hidden="true" />{title}
      </p>
      <p className="mt-2 text-sm text-zinc-600">{text || "–"}</p>
    </div>
  );
}

export function V3Aside({ subjectTitle = "Zuständigkeit", subject, owner, note = PRUEFSTAND, extra }) {
  const { user } = useAuth();
  return (
    <>
      <AsideCard icon={FileText} title={subjectTitle} text={subject} tone="lavender" testId="aside-subject" />
      <AsideCard icon={UserRound} title="Verantwortung" text={owner || `${user?.name} · ${user?.role_label}`} testId="aside-owner" />
      <AsideCard icon={InfoIcon} title="Prüfstand" text={note} testId="aside-note" />
      {extra}
    </>
  );
}

export function AmberAside({ title, text }) {
  return <AsideCard icon={InfoIcon} title={title} text={text} tone="amber" testId="aside-amber" />;
}

export function Field({ label, children, testId }) {
  return (
    <div data-testid={testId} className="rounded-xl border border-[#E6E1F0] bg-white px-4 py-3">
      <p className="text-sm font-semibold text-zinc-500">{label}</p>
      <div className="mt-1 break-words text-base text-brand-purple">{children ?? "–"}</div>
    </div>
  );
}

export function Compare({ before, after, beforeLabel = "Bisher / Eigene Fassung", afterLabel = "Neu / Aktueller Vorschlag", testId }) {
  return (
    <div className="space-y-2.5" data-testid={testId}>
      <div className="rounded-xl bg-[#EEEAF7] px-4 py-4"><p className="text-sm font-semibold text-zinc-500">{beforeLabel}</p>
        <p className="mt-2 break-words text-lg font-bold text-brand-purple">{before || "–"}</p></div>
      <div className="rounded-xl bg-[#FCE9F1] px-4 py-4"><p className="text-sm font-semibold text-zinc-500">{afterLabel}</p>
        <p className="mt-2 break-words text-lg font-bold text-brand-purple">{after || "–"}</p></div>
    </div>
  );
}

const PILL = { warning: "bg-[#FFF1CC] text-[#7A4F00]", success: "bg-[#E3F6EA] text-[#17673A]", critical: "bg-red-100 text-red-800", neutral: "bg-[#EEEAF7] text-brand-purple" };
export function Pill({ children, tone = "warning", testId, className = "" }) {
  return <span data-testid={testId} className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${PILL[tone]} ${className}`}>{children}</span>;
}

export function Notice({ icon: Icon = InfoIcon, title, text, tone = "lavender", large, testId }) {
  const cls = tone === "amber" ? "bg-[#FFF6DD]" : "bg-[#EEEAF7]";
  return (
    <div data-testid={testId} role="note" className={`flex items-start gap-3 rounded-xl ${cls} px-4 ${large ? "py-5" : "py-3"}`}>
      <Icon className={`${large ? "h-8 w-8" : "mt-0.5 h-5 w-5"} shrink-0 text-brand-purple`} aria-hidden="true" />
      <div><p className={`font-bold text-brand-purple ${large ? "text-lg" : ""}`}>{title}</p><p className="mt-1 text-sm text-zinc-600">{text}</p></div>
    </div>
  );
}

export function CheckRow({ id, label, checked, onChange, disabled }) {
  return (
    <label htmlFor={id} className="flex min-h-[40px] items-center gap-3 text-base text-brand-purple">
      <input id={id} type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} data-testid={id}
        className="h-5 w-5 rounded border-[#D9D2E8] accent-[#D50B58]" />{label}
    </label>
  );
}

const RESULT_TONE = { bestanden: "success", nicht_bestanden: "critical", ausstehend: "warning" };
export function CheckCard({ title, hint, status, testId }) {
  const done = status === "bestanden";
  return (
    <div data-testid={testId} className="flex items-start gap-4 rounded-xl border border-[#E6E1F0] bg-white px-4 py-4">
      <span aria-hidden="true" className={`mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded border ${done ? "border-brand-magenta bg-brand-magenta text-white" : "border-[#D9D2E8] bg-white"}`}>{done ? "✓" : ""}</span>
      <div className="min-w-0 flex-1"><p className="font-bold text-brand-purple">{title}</p>
        <p className="mt-1 text-sm text-zinc-600">{hint}</p></div>
      <Pill tone={RESULT_TONE[status] || "neutral"}>{status === "nicht_bestanden" ? "Nicht bestanden" : status === "bestanden" ? "Bestanden" : "Ausstehend"}</Pill>
    </div>
  );
}

export function V3Actions({ primary, onPrimary, primaryDisabled, busy, secondary = "Abbrechen", onSecondary, testId }) {
  const navigate = useNavigate();
  return (
    <div className="flex flex-col gap-3 pt-2 sm:flex-row">
      {primary && (
        <Button type="button" onClick={onPrimary} disabled={primaryDisabled || busy} data-testid={`${testId}-primary`}
          className="h-11 rounded-lg bg-brand-magenta px-6 text-base font-bold transition-colors hover:bg-brand-magenta-dark sm:min-w-[17rem]">
          {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden="true" />}{primary}
        </Button>
      )}
      {secondary && (
        <Button type="button" variant="outline" onClick={onSecondary || (() => navigate(-1))} data-testid={`${testId}-secondary`}
          className="h-11 rounded-lg border-[#E6E1F0] bg-white px-6 text-base font-bold text-brand-purple sm:min-w-[10rem]">{secondary}</Button>
      )}
    </div>
  );
}

export function useRun() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const run = async (fn, success) => {
    setBusy(true);
    setError(null);
    try {
      const res = await fn();
      toast.success(success);
      return res ?? true;
    } catch (err) {
      setError(errText(err));
      return null;
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, run, setError };
}

export function ErrorLine({ error, testId }) {
  if (!error) return null;
  return <p role="alert" data-testid={testId} className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</p>;
}

export function TextArea({ id, label, value, onChange, required, help }) {
  return (
    <label htmlFor={id} className="flex flex-col gap-1">
      <span className="text-sm font-semibold text-brand-purple">{label}{required ? " *" : ""}</span>
      <textarea id={id} rows={3} value={value} onChange={(e) => onChange(e.target.value)} data-testid={id}
        className="rounded-xl border border-[#E6E1F0] bg-white px-3 py-2 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-magenta" />
      {help && <span className="text-xs text-zinc-600">{help}</span>}
    </label>
  );
}

// C118: an empty filter result names the active filter and offers a way back instead of a bare "no data".
export function FilterEmpty({ title = "Keine Vorgänge", filters = [], onReset, testId = "filter-empty" }) {
  const { user } = useAuth();
  return (
    <div data-testid={testId} className="space-y-2.5">
      <Notice large tone="amber" title={title} text={filters.map(([, v]) => v).filter(Boolean).join(" · ") || "Aktuelle Auswahl"} />
      {filters.map(([l, v]) => <Field key={l} label={l}>{v}</Field>)}
      <Field label="Zuständigkeit">{user?.name}</Field>
      <Field label="Nächster Schritt">{onReset ? "Filter ändern oder zurücksetzen" : "Es liegen derzeit keine Vorgänge vor."}</Field>
      {onReset && <V3Actions primary="Filter zurücksetzen" onPrimary={onReset} secondary={null} testId={testId} />}
    </div>
  );
}

export function FeatureRadios({ id, label, value, onChange, disabled }) {
  return (
    <fieldset className="rounded-xl border border-[#E6E1F0] bg-white px-4 py-3" data-testid={`feature-${id}`}>
      <legend className="sr-only">{label}</legend>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <div className="min-w-[12rem] flex-1">
          <p className="font-bold text-brand-purple" aria-hidden="true">{label}</p>
          <Pill className="mt-2" tone={value === "ja" ? "success" : value === "nein" ? "neutral" : "warning"} testId={`feature-${id}-state`}>{VALUE_TEXT[value]}</Pill>
        </div>
        {Object.entries(VALUE_TEXT).map(([v, t]) => (
          <label key={v} className="flex min-h-[44px] items-center gap-2 text-sm text-brand-purple">
            <input type="radio" name={`feature-${id}`} value={v} checked={value === v} disabled={disabled} onChange={() => onChange(v)}
              data-testid={`feature-${id}-${v}`} className="h-4 w-4 accent-[#D50B58]" />{t}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

export function Stepper({ id, label, icon: Icon, value, min = 0, max = 500, onChange }) {
  const set = (v) => onChange(Math.max(min, Math.min(max, v)));
  return (
    <div className="rounded-xl border border-[#E6E1F0] bg-white px-4 py-4" data-testid={id}>
      <p id={`${id}-label`} className="flex items-center gap-2 text-lg font-bold text-brand-purple">{Icon && <Icon className="h-5 w-5 text-brand-magenta" aria-hidden="true" />}{label}</p>
      <div className="mt-3 flex items-center gap-4">
        <Button type="button" variant="secondary" size="icon" onClick={() => set(value - 1)} disabled={value <= min} aria-label={`${label} verringern`} data-testid={`${id}-minus`}><Minus className="h-4 w-4" /></Button>
        <output aria-labelledby={`${id}-label`} aria-live="polite" className="min-w-[3rem] text-2xl font-bold text-brand-purple" data-testid={`${id}-value`}>{value}</output>
        <span className="flex-1" />
        <Button type="button" variant="secondary" size="icon" onClick={() => set(value + 1)} disabled={value >= max} aria-label={`${label} erhöhen`} data-testid={`${id}-plus`}><Plus className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}
